// The review and setup commands CapturePoint added after 0506496a, wired as the desktop's main
// process wires them (apps/desktop/src/main/main.ts) and answered by the engine host's
// SetupRequests and "find-document-table" arms. Unbound, each resolved `undefined`: Remove type
// read that as a removal, and Find the table as a document with no table.
import type {
  DocumentTypeRemovalRequest,
  FindDocumentTableRequest,
  JobDefinition,
} from '../shared/desktop-api';
import type { EngineCall } from './desktop-commands';

export function setupCommands(
  call: EngineCall,
): Record<string, (...args: never[]) => Promise<unknown>> {
  return {
    previewDocumentTypeRemoval: (jobId: string, typeId: string) =>
      call('document-type-removal-impact', { jobId, documentTypeId: typeId }),
    removeDocumentType: (jobId: string, request: DocumentTypeRemovalRequest) =>
      call('remove-document-type', {
        jobId,
        documentTypeRemoval: {
          typeId: request.typeId,
          expectedSchemaVersion: request.expectedSchemaVersion,
          // The desktop refuses an unconfirmed removal; pass the answer through for the engine to refuse too.
          confirmed: request.confirmed === true,
        },
      }),
    // Only the review setting of a draft for this same job is read; the engine uses the saved job for the rest.
    previewReviewRelease: (jobId: string, draft: JobDefinition) =>
      call('review-release-preview', { jobId, job: draft?.id === jobId ? draft : undefined }),
    // What the store released on its own and a reviewer's "Put back in review" (CapturePoint 9fdb5c31).
    listHeldWorkReleases: (jobId: string, since?: string) =>
      call('list-held-work-releases', { jobId, ...(since ? { query: since } : {}), limit: 20 }),
    reopenHeldWorkRelease: (jobId: string, releaseId: string) =>
      call('reopen-held-work-release', { jobId, query: releaseId }),
    noteOpenDocument: (jobId: string, documentId: string | null) =>
      call('note-open-document', { jobId, ...(documentId ? { documentId } : {}) }),
    findDocumentTable: (request: FindDocumentTableRequest) =>
      call('find-document-table', {
        jobId: request.jobId,
        batchId: request.batchId,
        documentId: request.documentId,
        documentTypes: [request.documentType],
        ...(request.region ? { tableRegion: request.region } : {}),
      }),
  };
}

/**
 * Job setup asks on every open whether an external system owns the job's structure, and locks the
 * schema while it cannot tell (CapturePoint's `job-destination-binding`; the engine answers null for
 * a Paige job today). The tenant's channel, on the new-job route, names no job and carries no such
 * command: a job made there has just been created and nothing owns it, which is the answer.
 */
export function destinationBinding(
  call: EngineCall,
  onTenantChannel: boolean,
): { getJobDestinationBinding: (jobId: string) => Promise<unknown> } {
  return {
    getJobDestinationBinding: (jobId: string) =>
      onTenantChannel ? Promise.resolve(null) : call('job-destination-binding', { jobId }),
  };
}
